import { Box, FormControl, Text, TextInput as PrimerTextInput, Label } from "@primer/react";
import { useState, useEffect, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { ArrowLeftIcon, ArrowRightIcon, EyeIcon, TrashIcon, PlusIcon } from "@primer/octicons-react";
import { dmMailTrungTuyenApi } from "../../api/dmMailTrungTuyenApi";
import { ketQuaThiDauVaoApi } from "../../api/ketQuaThiDauVaoApi";
import { ketQuaThiDauVaoChiTietApi } from "../../api/ketQuaThiDauVaoChiTietApi";
import { trangthai_gui_mail_trungtuyen } from "../../models/response/category/trangthai_gui_mail_trungtuyen";
import ComboxboxEmailConfig from "../../components/combobox-email-config";
import FileViewAndUpload from "../../components/file-view-and-upload";
import Button from "../../components/ui/button";
import Modal from "../../components/ui/modal";
import ModalActions from "../../components/ui/modal/ModalActions";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { NotifyHelper } from "../../helpers/toast";
import { dm_mail_trungtuyen } from "../../models/response/category/dm_mail_trungtuyen";
import { IHocSinh } from "../../models/response/crm-hocsinh/IHocsinh";
import { IEmailFile } from "../../models/response/email/IEmailFile";
import { KetQuaThiDauVaoChiTietItemResponse } from "../../models/response/quan-ly-thi/ex_ketquathidauvao_chitiet";
import TextInput from "../../components/ui/text-input";
import DataTable from "../../components/ui/data-table";
import moment from "moment";
import { ComboboxMailTrungTuyen } from "../../components/combobox-mail-trung-tuyen";
import HocSinhShortInfo from "../../components/hoc-sinh/HocSinhShortInfo";
import Steps, { IStepData } from "../../components/steps/Steps";
import { quanHeGiaDinhApi } from "../../api/quanHeGiaDinhApi";
import { SendMailTrungTuyenRequest, SendMailTrungTuyenResponse } from "../../models/request/email/SendMailTrungTuyenRequest";

interface SendMailTrungTuyenModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStudentIds: number[];
  students: IHocSinh[];
}

const SendMailTrungTuyenModal = ({
  isOpen,
  onClose,
  selectedStudentIds,
  students,
}: SendMailTrungTuyenModalProps) => {
  const { translate } = useCommonContext();
  const { nam_hoc } = useCommonSelectedHook();
  const [currentStepId, setCurrentStepId] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<dm_mail_trungtuyen | null>(null);
  const [previewStudent, setPreviewStudent] = useState<IHocSinh | null>(null);
  const [isShowPreview, setIsShowPreview] = useState(false);
  const [previewExamDetails, setPreviewExamDetails] = useState<KetQuaThiDauVaoChiTietItemResponse[]>([]);
  const [isLoadingPreviewScores, setIsLoadingPreviewScores] = useState(false);
  const [previewExamStatus, setPreviewExamStatus] = useState<string>("");
  const [previewExamDate, setPreviewExamDate] = useState<string>("");
  const [sendResult, setSendResult] = useState<{ success: number; failed: number; total: number; details?: Array<{ hocSinhId: number; success: boolean; message: string }> } | null>(null);
  interface EmailInfo {
    email: string;
    owner?: string; // Thông tin người sở hữu email (ví dụ: "Cha", "Mẹ", "Email liên hệ")
  }
  const [studentsWithParentEmail, setStudentsWithParentEmail] = useState<(IHocSinh & { parent_emails?: EmailInfo[] })[]>([]);
  const [isLoadingParentEmails, setIsLoadingParentEmails] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState<number | null>(null);
  const [editingEmailValue, setEditingEmailValue] = useState<string>("");
  const [emailHistoryMap, setEmailHistoryMap] = useState<Map<number, trangthai_gui_mail_trungtuyen[]>>(new Map());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [previewEmailHistory, setPreviewEmailHistory] = useState<trangthai_gui_mail_trungtuyen | null>(null);
  const [isShowEmailHistoryPreview, setIsShowEmailHistoryPreview] = useState(false);

  const steps: IStepData[] = [
    { id: 1, name: "Mẫu email", is_active: currentStepId === 1 },
    { id: 2, name: "Người nhận", is_active: currentStepId === 2 },
    { id: 3, name: "Kết quả gửi", is_active: currentStepId === 3 },
  ];

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const loadParentEmails = useCallback(async () => {
    setIsLoadingParentEmails(true);
    try {
      const studentsWithEmail = await Promise.all(
        students.map(async (student) => {
          try {
            const res = await quanHeGiaDinhApi.selectByHocSinh(student.id);
            // Sử dụng Map để lưu email và thông tin người sở hữu (key là email normalized)
            const emailMap = new Map<string, EmailInfo>();
            
            if (res.is_success && res.data && res.data.length > 0) {
              // Lấy tất cả email từ phụ huynh với thông tin quan hệ
              res.data.forEach((q: any) => {
                const email = q.ts_phuhuynh?.email;
                if (email && validateEmail(email)) {
                  const normalizedEmail = email.trim().toLowerCase();
                  const owner = q.ts_phuhuynh?.ho_ten 
                    ? `${q.ts_phuhuynh.ho_ten}${q.dm_quanhe?.ten_quanhe ? ` (${q.dm_quanhe.ten_quanhe})` : ""}`
                    : q.dm_quanhe?.ten_quanhe || "Phụ huynh";
                  
                  // Nếu email chưa có hoặc chưa có owner, thêm vào
                  if (!emailMap.has(normalizedEmail)) {
                    emailMap.set(normalizedEmail, {
                      email: email.trim(),
                      owner: owner
                    });
                  }
                }
              });
            }
            
            // Thêm email_lien_he nếu có và hợp lệ (không trùng)
            if (student.email_lien_he && validateEmail(student.email_lien_he)) {
              const normalizedEmail = student.email_lien_he.trim().toLowerCase();
              if (!emailMap.has(normalizedEmail)) {
                emailMap.set(normalizedEmail, {
                  email: student.email_lien_he.trim(),
                  owner: "Email liên hệ"
                });
              }
            }
            
            // Chuyển Map về Array
            const emails: EmailInfo[] = Array.from(emailMap.values());
            
            return { ...student, parent_emails: emails };
          } catch (error) {
            console.error(`Error loading parent email for student ${student.id}:`, error);
            const emails: EmailInfo[] = [];
            if (student.email_lien_he && validateEmail(student.email_lien_he)) {
              emails.push({
                email: student.email_lien_he.trim().toLowerCase(),
                owner: "Email liên hệ"
              });
            }
            return { ...student, parent_emails: emails };
          }
        })
      );
      setStudentsWithParentEmail(studentsWithEmail);
    } catch (error) {
      console.error("Error loading parent emails:", error);
      NotifyHelper.Error("Có lỗi xảy ra khi tải email phụ huynh");
      // Fallback: sử dụng email_lien_he từ học sinh
      setStudentsWithParentEmail(students.map(s => {
        const emails: EmailInfo[] = [];
        if (s.email_lien_he && validateEmail(s.email_lien_he)) {
          emails.push({
            email: s.email_lien_he.trim().toLowerCase(),
            owner: "Email liên hệ"
          });
        }
        return { ...s, parent_emails: emails };
      }));
    } finally {
      setIsLoadingParentEmails(false);
    }
  }, [students]);

  // Load email phụ huynh khi vào Step 2
  useEffect(() => {
    if (currentStepId === 2 && students.length > 0) {
      loadParentEmails();
    }
  }, [currentStepId, students, loadParentEmails]);

  // Load lịch sử gửi email
  const loadEmailHistory = useCallback(async (hocSinhIds: number[]) => {
    setIsLoadingHistory(true);
    try {
      const historyMap = new Map<number, trangthai_gui_mail_trungtuyen[]>();
      await Promise.all(
        hocSinhIds.map(async (id) => {
          try {
            const res = await dmMailTrungTuyenApi.getHistoryByHocSinhId(id);
            if (res.is_success && res.data) {
              // Sort theo thời gian tạo mới nhất trước
              const sortedData = [...res.data].sort((a, b) => {
                const timeA = a.created_time ? new Date(a.created_time).getTime() : 0;
                const timeB = b.created_time ? new Date(b.created_time).getTime() : 0;
                return timeB - timeA; // Mới nhất trước
              });
              historyMap.set(id, sortedData);
            }
          } catch (error) {
            console.error(`Error loading email history for student ${id}:`, error);
          }
        })
      );
      setEmailHistoryMap(new Map(historyMap)); // Tạo Map mới để trigger re-render
    } catch (error) {
      console.error("Error loading email history:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Load lịch sử khi vào Step 3
  useEffect(() => {
    if (currentStepId === 3 && students.length > 0) {
      // Load lại lịch sử khi vào step 3
      loadEmailHistory(students.map(s => s.id));
    }
  }, [currentStepId, students, loadEmailHistory]);
  
  // Reload lịch sử khi sendResult thay đổi (sau khi gửi email)
  useEffect(() => {
    if (sendResult && sendResult.details && sendResult.details.length > 0) {
      const hocSinhIds = sendResult.details.map(d => d.hocSinhId);
      if (hocSinhIds.length > 0) {
        // Reload sau một khoảng thời gian ngắn để đảm bảo DB đã commit
        const timer = setTimeout(() => {
          loadEmailHistory(hocSinhIds);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [sendResult, loadEmailHistory]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{
    template_id: number;
    subject: string;
    content: string;
    dm_cauhinhemail_id: number;
    files: IEmailFile[];
  }>({
    defaultValues: {
      template_id: 0,
      subject: "",
      content: "",
      dm_cauhinhemail_id: 0,
      files: [],
    },
  });

  const handleTemplateSelect = async (id: number, template?: dm_mail_trungtuyen) => {
    if (id > 0) {
      setIsLoadingTemplate(true);
      try {
        // Gọi API detail để lấy đầy đủ thông tin mẫu email
        const response = await dmMailTrungTuyenApi.detail(id);
        if (response.is_success && response.data) {
          const templateData = response.data;
          setSelectedTemplate(templateData);
          setValue("template_id", templateData.id, { shouldValidate: true });
          setValue("subject", templateData.email_subject || "", { shouldValidate: true });
          setValue("content", templateData.email_contents || "", { shouldValidate: true, shouldDirty: true });
        } else {
          NotifyHelper.Error("Không thể tải thông tin mẫu email");
        }
      } catch (error) {
        console.error("Error loading template detail:", error);
        NotifyHelper.Error(translate("Base.Label.Error"));
      } finally {
        setIsLoadingTemplate(false);
      }
    } else {
      setSelectedTemplate(null);
      setValue("template_id", 0);
      setValue("subject", "");
      setValue("content", "");
    }
  };

  const onSubmit = async (data: {
    template_id: number;
    subject: string;
    content: string;
    dm_cauhinhemail_id: number;
    files: IEmailFile[];
  }) => {
    if (currentStepId === 1) {
      // Validate step 1
      if (!selectedTemplate) {
        NotifyHelper.Error("Vui lòng chọn mẫu email");
        return;
      }
      if (!data.subject || !data.content) {
        NotifyHelper.Error("Vui lòng nhập đầy đủ thông tin email");
        return;
      }
      // Trigger validate để hiển thị lỗi ngay dưới control
      const isValidStep1 = await trigger(["template_id", "subject", "content", "dm_cauhinhemail_id"]);
      if (!isValidStep1) return;

      // Chuyển sang step 2
      setCurrentStepId(2);
      return;
    }

    if (currentStepId === 2) {
      // Validate step 2
      if (selectedStudentIds.length === 0) {
        NotifyHelper.Error("Vui lòng chọn ít nhất một học sinh");
        return;
      }

      // Kiểm tra có email nào không
      const studentsWithEmails = studentsWithParentEmail.filter(
        (s) => s.parent_emails && s.parent_emails.length > 0
      );
      if (studentsWithEmails.length === 0) {
        NotifyHelper.Error("Không có học sinh nào có email để gửi");
        return;
      }

      // Gửi email và chuyển sang step 3
      setIsLoading(true);
      try {
        const subject = (watch("subject") || "") as string;
        const content = (watch("content") || "") as string;
        const dmCauHinhEmailId = watch("dm_cauhinhemail_id") as number;

        // Chuẩn bị dữ liệu gửi email cho từng học sinh
        const studentsToSend: SendMailTrungTuyenRequest["students"] = [];

        for (const student of studentsWithEmails) {
          // Lấy kết quả thi đầu vào mới nhất
          let examDetails: KetQuaThiDauVaoChiTietItemResponse[] = [];
          let examStatus = "";
          let examDate = "";

          try {
            const resKetQua = await ketQuaThiDauVaoApi.selectByHocSinhNamHoc(
              student.id,
              student.nam_hoc || nam_hoc
            );

            if (resKetQua.is_success && resKetQua.data && resKetQua.data.length > 0) {
              const exams: any[] = resKetQua.data;
              const exam: any = exams
                .slice()
                .sort((a, b) => {
                  const dateA = a.ngay_thi ? new Date(a.ngay_thi).getTime() : 0;
                  const dateB = b.ngay_thi ? new Date(b.ngay_thi).getTime() : 0;
                  if (dateA !== dateB) {
                    return dateB - dateA;
                  }
                  return (b.id || 0) - (a.id || 0);
                })[0];

              examStatus = exam.trang_thai || "";
              examDate = exam.ngay_thi ? moment(exam.ngay_thi).format("DD/MM/YYYY") : "";

              // Lấy chi tiết điểm thi
              const resChiTiet = await ketQuaThiDauVaoChiTietApi.selectByKetQuaThiDauVao(exam.id);
              if (resChiTiet.is_success && resChiTiet.data) {
                examDetails = resChiTiet.data;
              }
            }
          } catch (error) {
            console.error(`Error loading exam data for student ${student.id}:`, error);
          }

          // Render nội dung email cho học sinh này
          const { renderedContent, renderedSubject } = renderEmailContent(
            student,
            content,
            subject,
            examDetails,
            examStatus,
            examDate
          );

          // Lấy danh sách email từ parent_emails
          const emails = (student.parent_emails || []).map((e) => e.email).filter(Boolean);

          if (emails.length > 0) {
            studentsToSend.push({
              hocSinhId: student.id,
              emails: emails,
              renderedSubject: renderedSubject,
              renderedContent: renderedContent,
            });
          }
        }

        if (studentsToSend.length === 0) {
          NotifyHelper.Error("Không có email nào để gửi");
          setIsLoading(false);
          return;
        }

        // Chuẩn bị attachments
        const attachments = (watch("files") || []).map((f) => ({
          fileName: f.ten_file || "",
          fileUrl: f.duong_dan || "",
        }));

        console.log("Attachments to send:", attachments);

        // Gọi API gửi email
        const request: SendMailTrungTuyenRequest = {
          subject: subject,
          content: content,
          dmCauHinhEmailId: dmCauHinhEmailId,
          students: studentsToSend,
          attachments: attachments.length > 0 ? attachments : undefined,
        };

        console.log("Request to send:", { ...request, students: request.students.length, attachments: request.attachments?.length });

        const response = await dmMailTrungTuyenApi.send(request);

        if (response.is_success && response.data) {
          const result = response.data as SendMailTrungTuyenResponse;
          setSendResult({
            success: result.successCount || 0,
            failed: result.failCount || 0,
            details: result.results || [],
            total: result.total || 0,
          });
          setCurrentStepId(3);
          NotifyHelper.Success(
            `Đã gửi email thành công cho ${result.successCount}/${result.total} học sinh`
          );
          
          // Load lịch sử gửi email cho tất cả học sinh (có delay nhỏ để đảm bảo DB đã commit)
          setTimeout(() => {
            loadEmailHistory(studentsWithEmails.map(s => s.id));
          }, 500);
        } else {
          NotifyHelper.Error(response.message || "Có lỗi xảy ra khi gửi email");
        }
      } catch (error: any) {
        console.error("Error sending emails:", error);
        NotifyHelper.Error(error?.message || translate("Base.Label.Error"));
      } finally {
        setIsLoading(false);
      }
      return;
    }
  };

  // Hàm thay thế các biến động trong email bằng dữ liệu học sinh
  // và chèn bảng điểm chi tiết nếu có @bang_diem_chi_tiet
  const renderEmailContent = (
    student: IHocSinh,
    content: string,
    subject: string,
    examDetails?: KetQuaThiDauVaoChiTietItemResponse[],
    examStatus?: string,
    examDate?: string
  ) => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();

    let renderedContent = content || "";
    let renderedSubject = subject || "";

    // Map các biến động - chỉ sử dụng các field có sẵn trong interface
    const replacements: { [key: string]: string } = {
      "@ho_ten": student.ho_ten || "",
      "@ma_hs": student.ma_hs || "",
      "@ma_hoc_sinh": student.ma_hs || "",
      "@ho_ten_hoc_sinh": student.ho_ten || "",
      "@ngay_sinh": student.ngay_sinh ? moment(student.ngay_sinh).format("DD/MM/YYYY") : "",
      "@gioi_tinh": student.gioi_tinh || "",
      "@ten_truong": student.ten_truong || "",
      "@ten_he": student.ten_he || "",
      "@ten_khoi": student.ten_khoi || "",
      "@ten_lop": student.ten_lop || "",
      "@nam_hoc": student.nam_hoc || "",
      "@thang": month.toString(),
      "@nam": year.toString(),
      "@ho_ten_cha": student.ho_ten_lien_he || "",
      "@dien_thoai_cha": student.dien_thoai_lien_he || "",
      "@email_cha": student.email_lien_he || "",
      "@ho_ten_me": student.ho_ten_lien_he || "",
      "@dien_thoai_me": student.dien_thoai_lien_he || "",
      "@email_me": student.email_lien_he || "",
      "@ket_qua_chung": examStatus || "",
      "@ngay_thi": examDate || "",
    };

    // Thay thế trong content
    Object.keys(replacements).forEach((key) => {
      const regex = new RegExp(key.replace("@", "\\@"), "gi");
      renderedContent = renderedContent.replace(regex, replacements[key]);
      renderedSubject = renderedSubject.replace(regex, replacements[key]);
    });

    // Xử lý placeholder @bang_diem_chi_tiet
    if (renderedContent.toLowerCase().includes("@bang_diem_chi_tiet")) {
      let tableHtml = "";

      if (examDetails && examDetails.length > 0) {
        const rowsHtml = examDetails
          .map((d) => {
            const score =
              (d.diem_thi_cuoi && d.diem_thi_cuoi.trim() !== "")
                ? d.diem_thi_cuoi
                : d.diem_thi;
            const comment = d.nhan_xet || "";
            return `
              <tr>
                <td style="border:1px solid #ddd;padding:8px;">${d.mon_thi || ""}</td>
                <td style="border:1px solid #ddd;padding:8px;text-align:center;">${score || ""}</td>
                <td style="border:1px solid #ddd;padding:8px;">${comment}</td>
              </tr>
            `;
          })
          .join("");

        tableHtml = `
          <table style="border-collapse:collapse;width:100%;font-size:14px;">
            <thead>
              <tr>
                <th style="border:1px solid #ddd;padding:8px;background:#f5f5f5;text-align:left;">Môn thi</th>
                <th style="border:1px solid #ddd;padding:8px;background:#f5f5f5;text-align:center;">Điểm</th>
                <th style="border:1px solid #ddd;padding:8px;background:#f5f5f5;text-align:left;">Nhận xét</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        `;
      } else {
        tableHtml = "<p>(Chưa có bảng điểm chi tiết)</p>";
      }

      const tableRegex = /@bang_diem_chi_tiet/gi;
      renderedContent = renderedContent.replace(tableRegex, tableHtml);
    }

    return { renderedContent, renderedSubject };
  };

  const handlePreview = async (student: IHocSinh) => {
    setPreviewStudent(student);
    setIsShowPreview(true);
    setPreviewExamStatus("");
    setPreviewExamDate("");

    const currentContent = (watch("content") || "") as string;
    const lowerContent = currentContent.toLowerCase();

    // Nếu không có placeholder bảng điểm, kết quả chung và ngày thi thì không cần load dữ liệu thi
    if (
      !lowerContent.includes("@bang_diem_chi_tiet") &&
      !lowerContent.includes("@ket_qua_chung") &&
      !lowerContent.includes("@ngay_thi")
    ) {
      setPreviewExamDetails([]);
      return;
    }

    setIsLoadingPreviewScores(true);
    try {
      // Lấy kết quả thi đầu vào theo học sinh và năm học hiện tại
      const resKetQua = await ketQuaThiDauVaoApi.selectByHocSinhNamHoc(
        student.id,
        student.nam_hoc || nam_hoc
      );

      if (resKetQua.is_success && resKetQua.data && resKetQua.data.length > 0) {
        // Lấy lần thi mới nhất (ưu tiên theo ngày thi, nếu không có thì theo id lớn nhất)
        const exams: any[] = resKetQua.data;
        const exam: any = exams
          .slice()
          .sort((a, b) => {
            const dateA = a.ngay_thi ? new Date(a.ngay_thi).getTime() : 0;
            const dateB = b.ngay_thi ? new Date(b.ngay_thi).getTime() : 0;

            if (dateA !== dateB) {
              return dateB - dateA; // mới nhất trước
            }

            return (b.id || 0) - (a.id || 0);
          })[0];
        setPreviewExamStatus(exam.ten_trangthai_thisinh || "");
        setPreviewExamDate(exam.ngay_thi ? moment(exam.ngay_thi).format("DD/MM/YYYY") : "");
        const resChiTiet = await ketQuaThiDauVaoChiTietApi.selectByKetQuaThiDauVao(
          exam.id
        );

        if (resChiTiet.is_success && resChiTiet.data) {
          setPreviewExamDetails(resChiTiet.data);
        } else {
          setPreviewExamDetails([]);
        }
      } else {
        setPreviewExamDetails([]);
      }
    } catch (error) {
      console.error("Error loading exam details for preview:", error);
      NotifyHelper.Error("Có lỗi xảy ra khi tải bảng điểm chi tiết");
      setPreviewExamDetails([]);
    } finally {
      setIsLoadingPreviewScores(false);
    }
  };

  const columns = [
    {
      id: "hoc_sinh",
      dataField: "ho_ten",
      caption: "Học sinh",
      width: 150,
      cellRender: (item: IHocSinh) => <HocSinhShortInfo data={item} />,
    },
    {
      id: "thong_tin",
      dataField: "ten_lop",
      caption: "Thông tin",
      width: 150,
      cellRender: (item: IHocSinh) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "baseline",
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Box sx={{ fontWeight: 600 }}>{item.ten_lop}</Box>
          </Box>
          <Box sx={{ color: "fg.muted" }}>
            {[item.ten_khoi, item.ten_he ? `Hệ ${item.ten_he}` : ""]
              .filter(Boolean)
              .join(", ")}
          </Box>
        </Box>
      ),
    },
    {
      id: "preview",
      caption: "",
      width: 100,
      align: "center" as const,
      cellRender: (item: IHocSinh) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Button
            text="Xem trước"
            leadingVisual={EyeIcon}
            disabled={!selectedTemplate}
            onClick={() => handlePreview(item)}
            size="small"
            variant="primary"
          />
        </Box>
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gửi email trúng tuyển"
      width="large"
      sx={{
        width: "90%",
        maxWidth: "1200px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", padding: 0 }}
      >
        <Box sx={{ p: 0, pb: 2 }}>
          <Steps
            steps={steps}
            onChangedStep={(id) => {
              if (id <= currentStepId) {
                setCurrentStepId(id);
              }
            }}
          />
        </Box>
        <Box sx={{ p: 0, pt: 2, flex: 1, overflowY: "auto", minHeight: 0 }}>
          {currentStepId === 1 && (
            <Box sx={{ 
              display: "flex", 
              gap: 0
            }}>
            {/* Cột trái */}
            <Box sx={{ 
              width: "41.67%",
              display: "flex", 
              flexDirection: "column",
              pr: 2,
              borderRight: "1px solid",
              borderColor: "border.default"
            }}>
              {/* Chọn mẫu email */}
              <Box sx={{ mb: 3 }}>
                <FormControl>
                  <FormControl.Label>
                    Chọn mẫu email trúng tuyển <Text as="span" sx={{ color: "danger.fg" }}>*</Text>
                  </FormControl.Label>
                  <Controller
                    name="template_id"
                    control={control}
                    rules={{
                      required: "Vui lòng chọn mẫu email",
                      validate: (value) => {
                        if (!value || value === 0) {
                          return "Vui lòng chọn mẫu email";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <ComboboxMailTrungTuyen
                          value={field.value}
                          onValueChanged={async (id, data) => {
                            field.onChange(id);
                            await handleTemplateSelect(id, data);
                          }}
                          isShowClearButton={true}
                        />
                        {isLoadingTemplate && (
                          <FormControl.Caption>
                            Đang tải nội dung mẫu email...
                          </FormControl.Caption>
                        )}
                        {errors.template_id && (
                          <FormControl.Validation variant="error">
                            {errors.template_id.message}
                          </FormControl.Validation>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Box>

              {/* Gửi từ email */}
              <Box sx={{ mb: 3 }}>
                <FormControl>
                  <FormControl.Label>
                    Gửi từ email <Text as="span" sx={{ color: "danger.fg" }}>*</Text>
                  </FormControl.Label>
                  <Controller
                    control={control}
                    name="dm_cauhinhemail_id"
                    rules={{
                      validate: (value) => (value && value > 0) || "Vui lòng chọn email gửi đi",
                    }}
                    render={({ field }) => (
                      <>
                        <ComboxboxEmailConfig
                          value={field.value}
                          onValueChanged={(id) => {
                            field.onChange(id);
                          }}
                        />
                        {errors.dm_cauhinhemail_id && (
                          <FormControl.Validation variant="error">
                            {errors.dm_cauhinhemail_id.message}
                          </FormControl.Validation>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Box>

                {/* Đính kèm file */}
                <Box sx={{ mb: 3 }}>
                <FormControl>
                  <FormControl.Label>Đính kèm file</FormControl.Label>
                  <Controller
                    control={control}
                    name="files"
                    rules={{
                      required: {
                        value: false,
                        message: "",
                      },
                    }}
                    render={({ field }) => (
                      <FileViewAndUpload
                        accept="*"
                        files={
                          field.value?.map((x) => {
                            return {
                              file_name: x.ten_file,
                              url: x.duong_dan,
                            };
                          }) ?? []
                        }
                        onValueChanged={(files) => {
                          field.onChange(
                            files.map((x) => {
                              const obj: IEmailFile = {
                                duong_dan: x.url,
                                ten_file: x.file_name,
                                ts_email_id: 0,
                                id: 0,
                              };
                              return obj;
                            })
                          );
                        }}
                        showEmptyMessage={false}
                      />
                    )}
                  />
                </FormControl>
              </Box>

              {/* Danh sách học sinh được chọn */}
              <Box sx={{ mb: 3 }}>
                <FormControl>
                  <FormControl.Label>
                    Danh sách học sinh ({students.length} học sinh)
                  </FormControl.Label>
                  <DataTable
                    data={students}
                    columns={columns}
                    paging={{ enable: true, pageSizeItems: [10, 20, 50] }}
                    searchEnable={false}
                  />
                </FormControl>
              </Box>
            </Box>

            {/* Cột phải */}
            <Box sx={{ 
              width: "58.33%",
              display: "flex", 
              flexDirection: "column",
              pl: 2,
              flex: 1,
              minHeight: 0
            }}>
              {/* Tiêu đề email */}
              <Box sx={{ mb: 3 }}>
                <FormControl>
                  <FormControl.Label>
                    Tiêu đề email <Text as="span" sx={{ color: "danger.fg" }}>*</Text>
                  </FormControl.Label>
                  <TextInput
                    name="subject"
                    block
                    register={register}
                    errors={errors}
                    required
                    validateMessage="Vui lòng nhập tiêu đề email"
                  />
                </FormControl>
              </Box>              

              {/* Nội dung email */}
              <FormControl sx={{ mb: 3, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <FormControl.Label>
                  Nội dung email <Text as="span" sx={{ color: "danger.fg" }}>*</Text>
                </FormControl.Label>
                <Box sx={{        
                  border: "1px solid",
                  borderColor: "border.default",
                  borderRadius: 2,
                  width: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0
                }}>
                  <Controller
                    control={control}
                    name="content"
                    rules={{
                      required: {
                        value: true,
                        message: "Vui lòng nhập nội dung email",
                      },
                    }}
                    render={({ field }) => (
                      <Box sx={{ 
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        "& .se-wrapper": {
                          width: "100%",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0
                        },
                        "& .se-container": {
                          width: "100%",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0
                        },
                        "& .se-contents": {
                          width: "100%",
                          flex: 1,
                          overflowY: "auto"
                        }
                      }}>
                        <SunEditor
                          key={selectedTemplate?.id || "empty"}
                          setContents={field.value || ""}
                          onChange={(content) => {
                            field.onChange(content);
                          }}
                          setOptions={{
                            minHeight: "300px",
                            maxHeight: "none",
                            buttonList: [
                              ["undo", "redo"],
                              ["font", "fontSize"],
                              ["bold", "underline", "italic", "strike", "subscript", "superscript"],
                              ["fontColor", "hiliteColor"],
                              ["align", "list", "lineHeight"],
                              ["outdent", "indent"],
                              ["table", "horizontalRule", "link", "image"],               
                            ],
                            defaultTag: "div",
                            showPathLabel: false,
                            width: "100%",
                          }}
                        />
                      </Box>
                    )}
                  />
                </Box>
                {errors.content && (
                  <FormControl.Validation variant="error">
                    {errors.content.message}
                  </FormControl.Validation>
                )}             
              </FormControl>

            
            </Box>
          </Box>
          )}

          {currentStepId === 2 && (
            <Box>
              <DataTable
                height={`${window.innerHeight - 440}px`}
                data={studentsWithParentEmail.length > 0 ? studentsWithParentEmail : students.map(s => {
                  const emails: EmailInfo[] = [];
                  if (s.email_lien_he && validateEmail(s.email_lien_he)) {
                    emails.push({
                      email: s.email_lien_he.trim().toLowerCase(),
                      owner: "Email liên hệ"
                    });
                  }
                  return { ...s, parent_emails: emails };
                })}
                isLoading={isLoadingParentEmails}
                columns={[
                  {
                    id: "hoc_sinh",
                    dataField: "ho_ten",
                    caption: "Học sinh",
                    width: 200,
                    cellRender: (item: IHocSinh & { parent_emails?: EmailInfo[] }) => <HocSinhShortInfo data={item} />,
                  },
                  {
                    id: "thong_tin",
                    dataField: "ten_lop",
                    caption: "Thông tin",
                    width: 200,
                    cellRender: (item: IHocSinh & { parent_emails?: string[] }) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "baseline",
                        }}
                      >
                        <Box sx={{ display: "flex" }}>
                          <Box sx={{ fontWeight: 600 }}>{item.ten_lop}</Box>
                        </Box>
                        <Box sx={{ color: "fg.muted" }}>
                          {[item.ten_khoi, item.ten_he ? `Hệ ${item.ten_he}` : ""]
                            .filter(Boolean)
                            .join(", ")}
                        </Box>
                      </Box>
                    ),
                  },
                  {
                    id: "email",
                    dataField: "parent_emails",
                    caption: "Email phụ huynh",
                    width: 350,
                    cellRender: (item: IHocSinh & { parent_emails?: EmailInfo[] }) => {
                      const isEditing = editingEmailId === item.id;
                      const emails = item.parent_emails || [];

                      if (isEditing) {
                        return (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              <PrimerTextInput
                                value={editingEmailValue}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setEditingEmailValue(e.target.value);
                                }}
                                placeholder="Nhập email"
                                sx={{ flex: 1 }}
                                type="email"
                                validationStatus={editingEmailValue && !validateEmail(editingEmailValue) ? "error" : undefined}
                              />
                              <Button
                                text="Thêm"
                                size="small"
                                variant="primary"
                                leadingVisual={PlusIcon}
                                onClick={() => {
                                  const trimmedEmail = editingEmailValue.trim();
                                  if (!trimmedEmail) {
                                    NotifyHelper.Error("Vui lòng nhập email");
                                    return;
                                  }
                                  if (!validateEmail(trimmedEmail)) {
                                    NotifyHelper.Error("Email không hợp lệ");
                                    return;
                                  }
                                  // Kiểm tra duplicate (case-insensitive)
                                  const normalizedNewEmail = trimmedEmail.toLowerCase();
                                  const normalizedEmails = emails.map(e => e.email.toLowerCase());
                                  if (normalizedEmails.includes(normalizedNewEmail)) {
                                    NotifyHelper.Error("Email đã tồn tại");
                                    return;
                                  }
                                  const updated = studentsWithParentEmail.map((s) =>
                                    s.id === item.id ? { 
                                      ...s, 
                                      parent_emails: [...emails, {
                                        email: trimmedEmail.toLowerCase(),
                                        owner: ""
                                      }]
                                    } : s
                                  );
                                  setStudentsWithParentEmail(updated);
                                  setEditingEmailValue("");
                                  setEditingEmailId(null); // Tự động ẩn input sau khi thêm thành công
                                }}
                              />
                            </Box>
                            {editingEmailValue && !validateEmail(editingEmailValue) && (
                              <Text sx={{ fontSize: 0, color: "danger.fg" }}>
                                Email không hợp lệ
                              </Text>
                            )}
                          </Box>
                        );
                      }

                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                          {emails.length > 0 ? (
                            emails.map((emailInfo, index) => (
                              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Text sx={{ fontSize: 0 }}>
                                    {emailInfo.email}
                                    {emailInfo.owner && (
                                      <Text as="span" sx={{ color: "fg.muted", ml: 1, fontSize: 0 }}>
                                        - {emailInfo.owner}
                                      </Text>
                                    )}
                                  </Text>
                                </Box>
                                <Button
                                  size="small"
                                  variant="invisible"
                                  icon={TrashIcon}
                                  aria-label="Xóa email"
                                  onClick={() => {
                                    const updated = studentsWithParentEmail.map((s) =>
                                      s.id === item.id 
                                        ? { ...s, parent_emails: emails.filter((_, i) => i !== index) } 
                                        : s
                                    );
                                    setStudentsWithParentEmail(updated);
                                  }}
                                  sx={{ color: "danger.fg", flexShrink: 0 }}
                                />
                              </Box>
                            ))
                          ) : (
                            <Text sx={{ color: "fg.muted", fontSize: 0 }}>Chưa có email</Text>
                          )}
                          <Button
                            text="Thêm email"
                            size="small"
                            variant="default"
                            leadingVisual={PlusIcon}
                            onClick={() => {
                              setEditingEmailId(item.id);
                              setEditingEmailValue("");
                            }}
                          />
                        </Box>
                      );
                    },
                  },
                ]}
                paging={{ enable: true, pageSizeItems: [10, 20, 50, 100] }}
                searchEnable={false}
                subTitleComponent={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Text sx={{ mr: 2, color: "fg.muted" }}>
                      Tổng số: {students.length} học sinh
                    </Text>
                    {isLoadingParentEmails && (
                      <Text sx={{ color: "fg.muted" }}>
                        Đang tải email phụ huynh...
                      </Text>
                    )}
                  </Box>
                }
              />
            </Box>
          )}

          {currentStepId === 3 && (
            <Box>
              {sendResult ? (
                <Box>
                  {/* Tổng quan kết quả - Industrial & Simple */}
                  <Box sx={{ 
                    display: "flex", 
                    gap: 2, 
                    mb: 3,
                    alignItems: "center"
                  }}>
                    <Label size="large" variant="accent">
                      Tổng số: <Text sx={{ fontWeight: 600, ml: 1 }}>{sendResult.total}</Text>
                    </Label>
                    <Label size="large" variant="success">
                      Thành công: <Text sx={{ fontWeight: 600, ml: 1 }}>{sendResult.success}</Text>
                    </Label>
                    <Label size="large" variant={sendResult.failed > 0 ? "danger" : "severe"}>
                      Thất bại: <Text sx={{ fontWeight: 600, ml: 1 }}>{sendResult.failed}</Text>
                    </Label>
                  </Box>

                  {/* Chi tiết từng học sinh */}
                  {sendResult.details && sendResult.details.length > 0 && (
                    <Box>
                      <DataTable
                        height={`${window.innerHeight - 480}px`}
                        data={sendResult.details.map((detail) => {
                          const student = students.find(s => s.id === detail.hocSinhId);
                          const studentWithEmail = studentsWithParentEmail.find(s => s.id === detail.hocSinhId);
                          const emails = studentWithEmail?.parent_emails || [];
                          return {
                            ...detail,
                            student: student,
                            emails: emails,
                          };
                        })}
                        columns={[
                          {
                            id: "hoc_sinh",
                            dataField: "student",
                            caption: "Học sinh",
                            width: 200,
                            cellRender: (item: { student?: IHocSinh }) => {
                              if (!item.student) return <Text sx={{ fontSize: 0, color: "fg.muted" }}>—</Text>;
                              return <HocSinhShortInfo data={item.student} />;
                            },
                          },
                          {
                            id: "email_nhan",
                            dataField: "emails",
                            caption: "Email người nhận",
                            width: 280,
                            cellRender: (item: { emails?: EmailInfo[] }) => {
                              const emails = item.emails || [];
                              if (emails.length === 0) {
                                return <Text sx={{ fontSize: 0, color: "fg.muted" }}>—</Text>;
                              }
                              return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  {emails.slice(0, 2).map((emailInfo, idx) => (
                                    <Text key={idx} sx={{ fontSize: 0 }}>
                                      {emailInfo.email}
                                      {emailInfo.owner && (
                                        <Text as="span" sx={{ fontSize: 0, color: "fg.muted", ml: 1 }}>
                                          • {emailInfo.owner}
                                        </Text>
                                      )}
                                    </Text>
                                  ))}
                                  {emails.length > 2 && (
                                    <Text sx={{ fontSize: 0, color: "fg.muted" }}>
                                      +{emails.length - 2} email khác
                                    </Text>
                                  )}
                                </Box>
                              );
                            },
                          },
                          {
                            id: "trang_thai",
                            dataField: "success",
                            caption: "Trạng thái",
                            width: 90,
                            align: "center" as const,
                            cellRender: (item: { success: boolean }) => (
                              <Text sx={{ 
                                fontSize: 0, 
                                fontWeight: 500,
                                color: item.success ? "success.fg" : "danger.fg" 
                              }}>
                                {item.success ? "Thành công" : "Thất bại"}
                              </Text>
                            ),
                          },
                          {
                            id: "thong_bao",
                            dataField: "message",
                            caption: "Thông báo",
                            width: 150,
                            cellRender: (item: { message: string; success: boolean }) => (
                              <Text sx={{ 
                                fontSize: 0, 
                                color: item.success ? "fg.default" : "danger.fg"
                              }}>
                                {item.message}
                              </Text>
                            ),
                          },
                          {
                            id: "lich_su",
                            dataField: "hocSinhId",
                            caption: "Lịch sử gửi email",
                            width: 250,
                            cellRender: (item: { hocSinhId: number }) => {
                              const history = emailHistoryMap.get(item.hocSinhId) || [];
                              if (isLoadingHistory) {
                                return <Text sx={{ fontSize: 0, color: "fg.muted" }}>Đang tải...</Text>;
                              }
                              if (history.length === 0) {
                                return <Text sx={{ fontSize: 0, color: "fg.muted" }}>Chưa có lịch sử</Text>;
                              }
                              const successCount = history.filter(h => h.is_sended).length;
                              const totalCount = history.length;
                              const latestHistory = history[0]; // Đã được sort mới nhất trước
                              return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <Text sx={{ fontSize: 0 }}>
                                        Đã gửi: <Text as="span" sx={{ fontWeight: 600, color: "success.fg" }}>{successCount}</Text>/{totalCount}
                                      </Text>
                                      {latestHistory && (
                                        <Text sx={{ fontSize: 0, color: "fg.muted" }}>
                                          Lần cuối: {latestHistory.created_time ? moment(latestHistory.created_time).format("DD/MM/YYYY HH:mm") : "—"}
                                        </Text>
                                      )}
                                    </Box>
                                    <Button
                                      size="small"
                                      variant="invisible"
                                      icon={EyeIcon}
                                      aria-label="Xem nội dung email"
                                      onClick={() => {
                                        setPreviewEmailHistory(latestHistory);
                                        setIsShowEmailHistoryPreview(true);
                                      }}
                                      sx={{ flexShrink: 0 }}
                                    />
                                  </Box>
                                </Box>
                              );
                            },
                          },
                        ]}
                        paging={{ enable: true, pageSizeItems: [10, 20, 50, 100] }}
                        searchEnable={false}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Text sx={{ color: "fg.muted", fontSize: 1 }}>Chưa có kết quả gửi email</Text>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <ModalActions>
            {currentStepId === 1 && (
              <>
                <Button text={translate("Base.Label.Close")} onClick={onClose} />
                <Button
                  text="Tiếp tục"
                  variant="primary"
                  type="submit"
                  isLoading={isLoading}
                  trailingVisual={ArrowRightIcon}
                />
              </>
            )}
            {currentStepId === 2 && (
              <>
                <Button
                  text="Quay lại"
                  onClick={() => setCurrentStepId(1)}
                  leadingVisual={ArrowLeftIcon}
                />
                <Button
                  text="Gửi email"
                  variant="primary"
                  type="submit"
                  isLoading={isLoading}
                  trailingVisual={ArrowRightIcon}
                />
              </>
            )}
            {currentStepId === 3 && (
              <Button text={translate("Base.Label.Close")} onClick={onClose} />
            )}
          </ModalActions>
        </Box>
      </form>

      {/* Modal xem trước email */}
      {isShowPreview && previewStudent && (
        <Modal
          isOpen={isShowPreview}
          onClose={() => {
            setIsShowPreview(false);
            setPreviewStudent(null);
          }}
          title={`Xem trước email - ${previewStudent.ho_ten}`}
          width="large"
          sx={{
            width: "80%",
            maxWidth: "900px",
            height: "85vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 0, flex: 1, overflowY: "auto", minHeight: 0 }}>
            {(() => {
              const subject = (watch("subject") || "") as string;
              const content = (watch("content") || "") as string;
              const { renderedContent, renderedSubject } = renderEmailContent(
                previewStudent,
                content,
                subject,
                previewExamDetails,
                previewExamStatus,
                previewExamDate
              );

              return (
                <>
                  <Box sx={{ mb: 3 }}>
                    <FormControl>
                      <FormControl.Label>Tiêu đề email</FormControl.Label>
                      <Box
                        sx={{
                          p: 2,
                          bg: "canvas.subtle",
                          border: "1px solid",
                          borderColor: "border.default",
                          borderRadius: 2,
                        }}
                      >
                        <Text>{renderedSubject}</Text>
                      </Box>
                    </FormControl>
                  </Box>

                  <Box>
                    <FormControl>
                      <FormControl.Label>Nội dung email</FormControl.Label>
                      <Box
                        sx={{
                          p: 2,
                          bg: "canvas.subtle",
                          border: "1px solid",
                          borderColor: "border.default",
                          borderRadius: 2,
                          minHeight: "300px",
                        }}
                        dangerouslySetInnerHTML={{ __html: renderedContent }}
                      />
                    </FormControl>
                  </Box>

                  {isLoadingPreviewScores && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl.Caption>
                        Đang tải bảng điểm chi tiết...
                      </FormControl.Caption>
                    </Box>
                  )}
                </>
              );
            })()}
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <ModalActions>
              <Button
                text={translate("Base.Label.Close")}
                onClick={() => {
                  setIsShowPreview(false);
                  setPreviewStudent(null);
                }}
              />
            </ModalActions>
          </Box>
        </Modal>
      )}

      {/* Modal xem nội dung email đã gửi */}
      {isShowEmailHistoryPreview && previewEmailHistory && (
        <Modal
          isOpen={isShowEmailHistoryPreview}
          onClose={() => {
            setIsShowEmailHistoryPreview(false);
            setPreviewEmailHistory(null);
          }}
          title="Nội dung email đã gửi"
          width="large"
          sx={{
            width: "80%",
            maxWidth: "900px",
            height: "85vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 0, flex: 1, overflowY: "auto", minHeight: 0 }}>
            {/* Thông tin email - 3 trường cùng 1 dòng */}
            <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <FormControl>
                  <FormControl.Label>Thời gian gửi</FormControl.Label>
                  <Box
                    sx={{
                      p: 2,
                      bg: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 2,
                      width: "100%",

                    }}
                  >
                    <Text>
                      {previewEmailHistory.created_time
                        ? moment(previewEmailHistory.created_time).format("DD/MM/YYYY HH:mm:ss")
                        : "—"}
                    </Text>
                  </Box>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1 }}>
                <FormControl>
                  <FormControl.Label>Trạng thái</FormControl.Label>
                  <Box
                    sx={{
                      p: 2,
                      bg: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 2,
                      width: "100%",

                    }}
                  >
                    <Text sx={{ 
                      fontWeight: 500,
                      color: previewEmailHistory.is_sended ? "success.fg" : "danger.fg"
                    }}>
                      {previewEmailHistory.is_sended ? "Đã gửi thành công" : "Gửi thất bại"}
                    </Text>
                  </Box>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1 }}>
                <FormControl>
                  <FormControl.Label>Email người nhận</FormControl.Label>
                  <Box
                    sx={{
                      p: 2,
                      bg: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 2,
                      width: "100%",

                    }}
                  >
                    {previewEmailHistory.emails_to ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {previewEmailHistory.emails_to.split(";").filter(e => e.trim()).map((email, idx) => (
                          <Text key={idx} sx={{  }}>
                            {email.trim()}
                          </Text>
                        ))}
                      </Box>
                    ) : (
                      <Text sx={{ fontSize: 0, color: "fg.muted" }}>—</Text>
                    )}
                  </Box>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControl>
                <FormControl.Label>Tiêu đề email</FormControl.Label>
                <Box
                  sx={{
                    p: 2,
                    bg: "canvas.subtle",
                    border: "1px solid",
                    borderColor: "border.default",
                    borderRadius: 2,
                    width: "100%",

                  }}
                >
                  <Text>{previewEmailHistory.email_subject || "—"}</Text>
                </Box>
              </FormControl>
            </Box>

            <Box>
              <FormControl>
                <FormControl.Label>Nội dung email</FormControl.Label>
                <Box
                  sx={{
                    p: 2,
                    bg: "canvas.subtle",
                    border: "1px solid",
                    borderColor: "border.default",
                    borderRadius: 2,
                    minHeight: "300px",
                  }}
                  dangerouslySetInnerHTML={{ __html: previewEmailHistory.email_contents || "" }}
                />
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <ModalActions>
              <Button
                text={translate("Base.Label.Close")}
                onClick={() => {
                  setIsShowEmailHistoryPreview(false);
                  setPreviewEmailHistory(null);
                }}
              />
            </ModalActions>
          </Box>
        </Modal>
      )}
    </Modal>
  );
};

export default SendMailTrungTuyenModal;

